import { Box } from '@chakra-ui/react'
import { useTimelineDimensions } from '../hooks/useTimelineDimensions'
import { GreyGrey10, GreyWhite, ShadowXs } from '@sequencehq/design-tokens'
import { useAtom } from 'jotai'
import { expandedAtom, hoverPositionAtom } from '../atoms/timelineUi.atom'
import {
  DAY_RESOLUTION_PX,
  TIMELINE_DATE_OFFSET_FORMAT,
  TIMELINE_SCROLLBAR_HEIGHT_PX,
  TIMELINE_WRAPPER_HEIGHT_PX
} from '../timeline.constants'
import { ScheduleTimelineMonths } from './ScheduleTimelineMonths'
import { ScheduleTimelineWeeks } from './ScheduleTimelineWeeks'
import { ScheduleTimelineDayIndicator } from './ScheduleTimelineDayIndicator'
import { format, parse } from '@sequencehq/utils/dates'
import { useMemo } from 'react'
import { timelineDayOffsetsAtom } from '../atoms/timeline.atom'

const useScheduleTimelineAxis = () => {
  const [timelineDayOffsets] = useAtom(timelineDayOffsetsAtom)
  const [hoverPosition] = useAtom(hoverPositionAtom)

  /**
   * Use the timeline days offsets in order to provide the date
   * of the hovered position. We'll use this in our 'day indicator'
   * line that tracks the mouse.
   */
  const hoverDate = useMemo(() => {
    if (!hoverPosition) {
      return null
    }

    const dayIndex = Math.floor(hoverPosition / DAY_RESOLUTION_PX)
    const offsetDate = Object.entries(timelineDayOffsets)[dayIndex]?.[0]

    if (!offsetDate) {
      return
    }

    return parse(
      Object.entries(timelineDayOffsets)[dayIndex]?.[0],
      TIMELINE_DATE_OFFSET_FORMAT,
      new Date()
    )
  }, [hoverPosition, timelineDayOffsets])

  const today = new Date()

  return {
    timelineDayOffsets,
    hoverDate,
    hoverPosition,
    today
  }
}

export const ScheduleTimelineAxis = () => {
  const hook = useScheduleTimelineAxis()
  const [expanded, setExpanded] = useAtom(expandedAtom)
  const { timelineContainerHeight } = useTimelineDimensions()

  return (
    <Box
      width={'100%'}
      cursor="pointer"
      backgroundColor={GreyWhite}
      boxShadow="0 0 0 0px rgba(0, 0, 0, 0.1)"
      _hover={{
        backgroundColor: GreyGrey10,
        boxShadow: ShadowXs
      }}
      transition={
        'background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out'
      }
      onClick={() => setExpanded(prevExpanded => !prevExpanded)}
    >
      <ScheduleTimelineMonths />
      <ScheduleTimelineWeeks />
      <ScheduleTimelineDayIndicator
        offset={
          hook.timelineDayOffsets[
            format(hook.today, TIMELINE_DATE_OFFSET_FORMAT)
          ]
        }
        date={hook.today}
        showLabel={expanded}
        theme="today"
        height={Math.max(
          timelineContainerHeight - TIMELINE_SCROLLBAR_HEIGHT_PX,
          TIMELINE_WRAPPER_HEIGHT_PX
        )}
      />
      {hook.hoverPosition && hook.hoverDate && expanded && (
        <ScheduleTimelineDayIndicator
          offset={hook.hoverPosition}
          date={hook.hoverDate}
          theme="hover"
          showLabel
          height={Math.max(
            timelineContainerHeight - TIMELINE_SCROLLBAR_HEIGHT_PX,
            TIMELINE_WRAPPER_HEIGHT_PX
          )}
        />
      )}
    </Box>
  )
}
